import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Container, Group, Select, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import HttpClient from "../helpers/HttpClient";
import { InventoryArticle, StorageLocation } from "../types/inventory.type";
import { ArticleCategory } from "../types/article-category.type";

// Stock overview (inventory right) — also used read-only as the restaurant stock
// view (restaurantOnly: drinks and meals, for the restaurant right).
function InventoryList({ restaurantOnly = false }: { restaurantOnly?: boolean }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<InventoryArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (restaurantOnly) return;
    new HttpClient<ArticleCategory>("/article-category").getAll().then(setCategories).catch(() => {});
    new HttpClient<StorageLocation>("/inventory/location").getAll().then(setLocations).catch(() => {});
  }, [restaurantOnly]);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (categoryId) params.categoryId = categoryId;
    if (locationId) params.locationId = locationId;
    if (showArchived) params.archived = "true";
    new HttpClient<InventoryArticle>(restaurantOnly ? "/inventory/restaurant" : "/inventory")
      .getAll(params)
      .then(setArticles)
      .catch(() => notifications.show({ color: "red", message: "Bestand konnte nicht geladen werden." }))
      .finally(() => setLoading(false));
  }, [restaurantOnly, categoryId, locationId, showArchived]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? articles.filter((a) => a.name.toLowerCase().includes(q)) : articles;
  }, [articles, search]);

  return (
    <Container fluid mt="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>{restaurantOnly ? "Restaurant – Bestand" : "Inventar – Bestand"}</Title>
        {!restaurantOnly && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate("/inventory/article/new")}>
            Neuer Artikel
          </Button>
        )}
      </Group>

      <Group mb="md" grow={false} wrap="wrap">
        <TextInput
          placeholder="Artikel suchen"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        {!restaurantOnly && (
          <>
            <Select
              placeholder="Kategorie"
              clearable
              data={categories.map((c) => ({ value: String(c.id), label: c.name }))}
              value={categoryId}
              onChange={setCategoryId}
            />
            <Select
              placeholder="Lagerort"
              clearable
              data={locations.map((l) => ({ value: String(l.id), label: l.name }))}
              value={locationId}
              onChange={setLocationId}
            />
            <Switch label="Archivierte anzeigen" checked={showArchived} onChange={(e) => setShowArchived(e.currentTarget.checked)} />
          </>
        )}
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        fetching={loading}
        records={visible}
        noRecordsText={restaurantOnly ? "Keine Kassen-Artikel" : "Keine Inventar-Artikel"}
        onRowClick={restaurantOnly ? undefined : ({ record }) => navigate(`/inventory/article/${record.id}`)}
        columns={[
          {
            accessor: "name",
            title: "Artikel",
            render: (a) => (
              <Stack gap={2}>
                <Text size="sm" fw={500}>{a.name}</Text>
                <Group gap={4}>
                  {a.isRentable && <Badge size="xs" variant="light" color="orange">Verleih</Badge>}
                  {a.showInCalendar && <Badge size="xs" variant="light" color="indigo">Kalender</Badge>}
                  {a.isRestaurantArticle && <Badge size="xs" variant="light" color="teal">Kasse</Badge>}
                  {!a.isStockTracked && <Badge size="xs" variant="outline" color="gray">Kein Bestand geführt</Badge>}
                  {a.isArchived && <Badge size="xs" variant="light" color="orange">Archiviert</Badge>}
                </Group>
              </Stack>
            ),
          },
          { accessor: "category", title: "Kategorie", render: (a) => a.category?.name ?? "–" },
          { accessor: "location", title: "Lagerort", render: (a) => a.location?.name ?? "–" },
          {
            accessor: "variants",
            title: "Varianten",
            render: (a) => {
              const active = a.variants.filter((v) => v.isActive);
              if (active.length <= 1) return "–";
              return (
                <Group gap={4}>
                  {active.map((v) => (
                    <Badge key={v.id} size="sm" variant="outline" color={v.available > 0 ? "gray" : "red"}>
                      {v.label}: {v.available}
                    </Badge>
                  ))}
                </Group>
              );
            },
          },
          { accessor: "stock", title: "Bestand", textAlign: "right", render: (a) => <StockValue value={a.stock} tracked={a.isStockTracked} /> },
          { accessor: "out", title: "Ausgegeben", textAlign: "right", render: (a) => (a.out ? a.out : "–") },
          { accessor: "available", title: "Verfügbar", textAlign: "right", render: (a) => <StockValue value={a.available} tracked={a.isStockTracked} /> },
        ]}
      />
    </Container>
  );
}

// Untracked articles have no stock (not "0").
function StockValue({ value, tracked }: { value: number; tracked: boolean }) {
  if (!tracked) return <Text size="sm" c="dimmed">–</Text>;
  return (
    <Text size="sm" fw={600} c={value <= 0 ? "red" : undefined}>
      {value}
    </Text>
  );
}

export default InventoryList;
